import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { authCheckUrl } from "config";
import type { Dispatch, Middleware, UnknownAction } from "@reduxjs/toolkit";
import Session from "users/__session/session";

// A thunk-like function that also receives SignalR invoke
export type SignalThunk<
  S = unknown,
  D extends Dispatch = Dispatch<UnknownAction>,
  R = void | Promise<void>
> = (dispatch: D, getState: () => S, invoke: HubConnection["invoke"]) => R;

// Callback for a server event that returns a SignalThunk
export type EventCallback<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> =
  (...args: unknown[]) => SignalThunk<S, D>;

export interface WithCallbacks<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> {
  add(name: string, callback: EventCallback<S, D>): WithCallbacks<S, D>;
  callbackMap: Map<string, EventCallback<S, D>>;
}

export const withCallbacks = <S = unknown, D extends Dispatch = Dispatch<UnknownAction>>(): WithCallbacks<S, D> => {
  const callbackMap = new Map<string, EventCallback<S, D>>();
  return {
    add(name: string, callback: EventCallback<S, D>) {
      callbackMap.set(name, callback);
      return this;
    },
    callbackMap,
  };
};

export interface MiddlewareConfig<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> {
  callbacks: WithCallbacks<S, D>;
  connection: HubConnection;
  onStart?: () => void;
  shouldConnectionStartImmediately?: boolean;
}

const isFunction = (value: unknown): value is (...args: unknown[]) => unknown => typeof value === "function";

export const connect = async (connection: HubConnection, onStart?: () => void): Promise<void> => {
  const session = Session.loadFromStorage(() => {});
  const token = session.authToken?.value;
  if (session.authToken?.isExpired) {
    session.logout();
  }
  if (!token) return; // don't connect unauthenticated
  if (connection.state !== HubConnectionState.Disconnected) return; // already connected/connecting

  try {
    await connection.start();
    onStart?.();
    console.log("SignalR connected");
  } catch (err) {
    const authCheckResponse = await fetch(authCheckUrl);
    if (authCheckResponse.status === 401) {
      console.warn("SignalR connection failed due to unauthorized session. Not retrying until re-login.");
      session.logout();
      return;
    }
    throw err;
  }
};

const reduxSignalRMiddleware =
  <S = unknown, D extends Dispatch = Dispatch<UnknownAction>>({
    callbacks,
    onStart,
    connection,
    shouldConnectionStartImmediately = true,
  }: MiddlewareConfig<S, D>): Middleware<object, S, D> =>
  (store) => {
    // Register handlers idempotently (remove previous handlers for each name first)
    for (const [name, callback] of callbacks.callbackMap) {
      connection.off(name);
      connection.on(name, (...args: unknown[]) => {
        try {
          const thunk = callback(...args);
          if (isFunction(thunk)) {
            void Promise.resolve(
              thunk(store.dispatch, store.getState as () => S, connection.invoke.bind(connection)),
            ).catch((err) => {
              console.error(`SignalR handler for "${name}" failed:`, err);
            });
          }
        } catch (err) {
          console.error(`SignalR callback for "${name}" threw:`, err);
        }
      });
    }

    if (shouldConnectionStartImmediately) {
      void connect(connection, onStart);
    }

    // Reconnect after auth lifecycle
    document?.addEventListener("loggedIn", () => {
      void connect(connection, onStart);
    });

    return (next) => (action) => {
      if (isFunction(action)) {
        // Treat function actions as SignalThunks
        try {
          return (action as unknown as SignalThunk<S, D>)(
            store.dispatch,
            store.getState as () => S,
            connection.invoke.bind(connection),
          );
        } catch (err) {
          console.error("SignalR thunk execution failed:", err);
          return undefined as unknown as D;
        }
      }
      return next(action as D);
    };
  };

export default reduxSignalRMiddleware;
